import { esMoneda, leerImporte, type Moneda } from '@mc/comercio'
import { useState } from 'react'

import { errorDelCampo } from '../api/cuenta'
import { consulta, ErrorDeLaApi, pedir } from '../api/nucleo'
import { Aviso, Boton, Campo, clases, Modal } from '../componentes/ui'
import { consecuenciasDeDevolver, dinero, nombreDePasarela } from './tiendaEnLlano'

/**
 * «Reembolsar» (CLAUDE.md §23): devolver todo lo que queda o una parte, con un motivo si se
 * quiere, y una confirmación que dice qué pasa con el dinero y con las existencias. El panel
 * no decide nada: el importe lo lee el núcleo con la misma regla (`leerImporte`), comprueba
 * que no pasa de lo que queda y se lo pide a la pasarela; aquí se adelanta para no hacer
 * esperar a nadie por una coma mal puesta, y el error del núcleo se pinta junto al importe.
 *
 * Vale igual para la seña o el total de una reserva (§33, `de: 'reserva'`): la reserva no cambia de estado.
 */
export type Devuelto = { importe: number; moneda: string; parcial: boolean; devuelto: number; queda: number; completo: boolean; reembolso: null | string }

const MAXIMO_DEL_MOTIVO = 300

export const DevolverDinero = ({
  de = 'pedido',
  espacio,
  pedido,
  cobrado,
  devuelto,
  queda,
  moneda,
  pasarela,
  comprador,
  estado,
  existenciasDescontadas,
  cerrar,
  hecho,
}: {
  cerrar: () => void
  cobrado: number
  comprador: string
  /** Qué se devuelve: un pedido (por omisión) o una reserva. `pedido` es su id en los dos casos. */
  de?: 'pedido' | 'reserva'
  devuelto: number
  espacio: number
  estado: unknown
  existenciasDescontadas: unknown
  hecho: (r: Devuelto) => void
  moneda: string
  pasarela: string
  pedido: number
  queda: number
}) => {
  const [modo, setModo] = useState<'parte' | 'todo'>('todo')
  const [importe, setImporte] = useState('')
  const [motivo, setMotivo] = useState('')
  const [paso, setPaso] = useState<'confirmar' | 'elegir'>('elegir')
  const [errorDelImporte, setErrorDelImporte] = useState<string | undefined>()
  const [fallo, setFallo] = useState<null | string>(null)
  const [enviando, setEnviando] = useState(false)
  const laMoneda: Moneda = esMoneda(moneda) ? moneda : 'PEN'
  const sinDecimales = laMoneda === 'CLP' || laMoneda === 'COP'

  /** Lo que se va a devolver, en céntimos, o el motivo por el que no vale. */
  const cuanto = (): { centimos: number } | { motivo: string } => {
    if (modo === 'todo') return { centimos: queda }
    const leido = leerImporte(importe, laMoneda)
    if ('motivo' in leido) return leido
    if (leido.centimos > queda) return { motivo: `No puedes devolver más de lo que queda por devolver: ${dinero(queda, laMoneda)}.` }
    return leido
  }

  const continuar = () => {
    setFallo(null)
    const c = cuanto()
    if ('motivo' in c) {
      setErrorDelImporte(c.motivo)
      return
    }
    setErrorDelImporte(undefined)
    setPaso('confirmar')
  }

  const devolver = async () => {
    setEnviando(true)
    setFallo(null)
    try {
      const ruta = de === 'reserva' ? `/agenda/reservas/reembolsar${consulta({ cliente: espacio, reserva: pedido })}` : `/pedidos/reembolsar${consulta({ cliente: espacio, pedido })}`
      const r = await pedir<Devuelto>(ruta, {
        metodo: 'POST',
        cuerpo: { ...(modo === 'parte' ? { importe } : {}), ...(motivo.trim() ? { motivo: motivo.trim() } : {}) },
      })
      hecho(r)
    } catch (e) {
      // Lo del importe, junto al importe (el núcleo dice cuánto queda si alguien devolvió mientras tanto).
      const delImporte = errorDelCampo(e, 'importe')
      if (delImporte) {
        setModo('parte')
        setErrorDelImporte(delImporte)
        setPaso('elegir')
      } else setFallo(e instanceof ErrorDeLaApi || e instanceof Error ? e.message : 'No se ha podido devolver el dinero. Vuelve a intentarlo.')
      setEnviando(false)
    }
  }

  const c = cuanto()
  const consecuencias = 'centimos' in c ? consecuenciasDeDevolver({ de, importe: c.centimos, queda, moneda: laMoneda, pasarela, comprador, estado, existenciasDescontadas }) : null

  return (
    <Modal
      ancho="max-w-lg"
      cerrar={() => !enviando && cerrar()}
      pie={
        paso === 'elegir' ? (
          <>
            <Boton onClick={cerrar}>No devolver nada</Boton>
            <Boton onClick={continuar} tipo="primario">
              Continuar
            </Boton>
          </>
        ) : (
          <>
            <Boton disabled={enviando} onClick={() => setPaso('elegir')}>
              Volver
            </Boton>
            <Boton cargando={enviando} onClick={() => void devolver()} tipo="primario">
              {'centimos' in c ? `Sí, devolver ${dinero(c.centimos, laMoneda)}` : 'Sí, devolver'}
            </Boton>
          </>
        )
      }
      titulo={paso === 'confirmar' && consecuencias ? consecuencias.titulo : 'Devolver dinero al comprador'}
    >
      {paso === 'elegir' ? (
        <div className="flex flex-col gap-4 text-sm">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-tenue">Cobrado con {nombreDePasarela(pasarela)}</dt>
            <dd className="text-right tabular-nums">{dinero(cobrado, laMoneda)}</dd>
            {devuelto > 0 && (
              <>
                <dt className="text-tenue">Ya devuelto</dt>
                <dd className="text-right tabular-nums">{dinero(devuelto, laMoneda)}</dd>
              </>
            )}
            <dt className="font-medium">Queda por devolver</dt>
            <dd className="text-right font-medium tabular-nums">{dinero(queda, laMoneda)}</dd>
          </dl>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-[13px] font-medium">Cuánto</legend>
            {(
              [
                ['todo', `Devolver todo lo que queda (${dinero(queda, laMoneda)})`, de === 'reserva' ? 'La reserva sigue como está: si además no va a venir, cancélala.' : 'El pedido queda como «Reembolsado».'],
                ['parte', 'Devolver una parte', de === 'reserva' ? 'Por ejemplo, la mitad de la seña. La reserva sigue como está.' : 'Por ejemplo, una unidad que llegó rota o el envío. El pedido sigue como está.'],
              ] as const
            ).map(([valor, etiqueta, ayuda]) => (
              <label
                className={clases('flex cursor-pointer items-start gap-3 rounded-caja border p-3 transition-colors', modo === valor ? 'border-violeta-borde bg-violeta-suave/50' : 'border-linea hover:border-linea-fuerte')}
                key={valor}
              >
                <input
                  checked={modo === valor}
                  className="mt-1"
                  name="cuanto"
                  onChange={() => {
                    setModo(valor)
                    setErrorDelImporte(undefined)
                  }}
                  type="radio"
                />
                <span className="flex flex-col">
                  <strong>{etiqueta}</strong>
                  <small className="text-tenue">{ayuda}</small>
                </span>
              </label>
            ))}
          </fieldset>

          {modo === 'parte' && (
            <Campo
              ayuda={`En ${laMoneda}${sinDecimales ? ', sin decimales' : ''}. Como mucho ${dinero(queda, laMoneda)}.`}
              autoFocus
              error={errorDelImporte}
              etiqueta={`Importe a devolver (${laMoneda})`}
              inputMode={sinDecimales ? 'numeric' : 'decimal'}
              onChange={(e) => {
                setImporte(e.target.value)
                setErrorDelImporte(undefined)
              }}
              onKeyDown={(e) => e.key === 'Enter' && continuar()}
              placeholder={sinDecimales ? '15000' : '25,50'}
              value={importe}
            />
          )}

          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Motivo (opcional)</span>
            <textarea
              className="campo min-h-16 w-full rounded-campo border border-linea bg-papel px-3 py-2 text-sm text-tinta placeholder:text-tenue hover:border-linea-fuerte"
              maxLength={MAXIMO_DEL_MOTIVO}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={`Por qué se devuelve. Queda apuntado en ${de === 'reserva' ? 'la reserva' : 'el pedido'}; a quien pagó no se le manda.`}
              value={motivo}
            />
          </label>
          {fallo && <Aviso tono="error">{fallo}</Aviso>}
        </div>
      ) : (
        consecuencias && (
          <div className="flex flex-col gap-3 text-sm text-tinta-2">
            <p>{consecuencias.dinero}</p>
            <p>{consecuencias.pedido}</p>
            {motivo.trim() && <p className="text-tenue">Motivo: «{motivo.trim()}»</p>}
            {fallo && <Aviso tono="error">{fallo}</Aviso>}
          </div>
        )
      )}
    </Modal>
  )
}
